import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import plumbingWork from '../../assets/home_page_service_images/plumbing_work.jpg';
import plumbingPipes from '../../assets/home_page_service_images/plumbing_pipes.jpg';
import electricalOutlet from '../../assets/home_page_service_images/electrical_outlet.jpg';
import electricalPanel from '../../assets/home_page_service_images/electrical_panel.jpg';
import hvacSystem from '../../assets/home_page_service_images/hvac_system.webp';
import hvacCommercial from '../../assets/home_page_service_images/hvac_commercial.jpg';
import landscapingMowing from '../../assets/home_page_service_images/landscaping_mowing_trimming.jpg';
import landscapingWheelbarrow from '../../assets/home_page_service_images/landsacping_wheelbarrel.jpg';
import ServiceSearchBar from '../ServiceSearchBar/ServiceSearchBar';
import { useAuth } from '../../context/AuthContext';
import {
  HeroBadge,
  HeroContent,
  HeroHeading,
  HeroPhoto,
  HeroPhotoImage,
  HeroSection,
  HeroSubheading,
  PhotoNote,
  ProLink,
  TrustItem,
  TrustRow,
} from './HomeHero.styled';

const TRUST_POINTS = [
  'Only Northern Kentucky businesses',
  'Free, no sign-up',
  'Your info goes only to the pro you pick',
];

const HERO_PHOTOS = [
  { src: plumbingWork, alt: 'A plumber repairing the drain under a sink' },
  { src: electricalOutlet, alt: 'An electrical outlet pulled from the wall with its wires exposed' },
  { src: hvacSystem, alt: 'A home air conditioner beside a house' },
  { src: landscapingMowing, alt: 'Landscapers mowing a lawn and trimming along a hedge' },
  { src: plumbingPipes, alt: 'Plumbing pipes and fittings' },
  { src: electricalPanel, alt: 'An electrician working inside an outdoor electrical panel' },
  { src: hvacCommercial, alt: 'Rooftop air conditioning units' },
  { src: landscapingWheelbarrow, alt: 'A landscaper pushing a wheelbarrow across a lawn' },
];

const HERO_PHOTO_INTERVAL_MS = 20 * 1000;

/** Home page hero: warm split layout with the headline, service search and a local photo. */
const HomeHero = () => {
  const { user, loading } = useAuth();
  const [photoIndex, setPhotoIndex] = useState(0);
  const photoIndexRef = useRef(0);

  useEffect(() => {
    if (HERO_PHOTOS.length < 2) return undefined;
    const timer = setInterval(() => {
      photoIndexRef.current = (photoIndexRef.current + 1) % HERO_PHOTOS.length;
      setPhotoIndex(photoIndexRef.current);
    }, HERO_PHOTO_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  return (
    <HeroSection>
      <HeroContent>
        <HeroBadge>
          <HomeRoundedIcon aria-hidden />
          Made for Northern Kentucky homes
        </HeroBadge>
        <HeroHeading>
          Home help from local pros <em>your neighbors trust.</em>
        </HeroHeading>
        <HeroSubheading>
          Tell us what&apos;s going on at home and see matched local pros in a couple of taps.
        </HeroSubheading>

        <ServiceSearchBar variant="hero" />

        <TrustRow>
          {TRUST_POINTS.map((point) => (
            <TrustItem key={point}>
              <CheckRoundedIcon aria-hidden />
              {point}
            </TrustItem>
          ))}
        </TrustRow>

        {!loading && !user && (
          <ProLink component={RouterLink} to="/provider-signup">
            Own a local business? Join as a pro →
          </ProLink>
        )}
      </HeroContent>

      <HeroPhoto>
        {HERO_PHOTOS.map((item, index) => (
          <HeroPhotoImage
            key={item.src}
            src={item.src}
            alt={index === photoIndex ? item.alt : ''}
            aria-hidden={index === photoIndex ? undefined : true}
            $active={index === photoIndex}
          />
        ))}
        <PhotoNote>
          <PlaceRoundedIcon aria-hidden />
          Serving Boone, Kenton and Campbell counties
        </PhotoNote>
      </HeroPhoto>
    </HeroSection>
  );
};

export default HomeHero;
